import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import '../../input.css'
import './NavigationBar.scss'
import useSiteRevampContent from '../../content/useSiteRevampContent'
import { getCtaContent } from '../../content/ctaContent'
import trackEvent from '../../utils/trackEvent'

const NavigationBar = () => {
  const [, i18n] = useTranslation()
  const content = useSiteRevampContent()
  const cta = getCtaContent(i18n.language)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleFlagClicked = (e) => {
    e.preventDefault()
    const language = e.currentTarget.getAttribute('data-tag')
    i18n.changeLanguage(language).catch((err) => console.error('Language change failed', err))
    setIsMenuOpen(false)
  }

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const FlagLink = ({ tag, image, alt }) => (
    <button onClick={handleFlagClicked} data-tag={tag} className="flex justify-center items-center">
      <img src={`/images/${image}.png`} alt={alt} className="h-5 w-5 m-1" />
    </button>
  )

  const TranslationFlags = ({ isMobile }) => (
    <div className={`flex ${isMobile ? 'flex-col items-center space-y-2' : 'flex-row items-center space-x-2'}`}>
      <FlagLink tag="pt-BR" image="pt-br" alt="Traduza para Portugues" />
      <FlagLink tag="en-US" image="en-gb" alt="Translate to English" />
      <FlagLink tag="es" image="es" alt="Traducir al espanol" />
    </div>
  )

  const navLinkCls = ({ isActive }) =>
    `transition-colors duration-200 ${isActive ? 'text-pyramid-19' : 'text-white hover:text-pyramid-19'}`

  const MenuOptions = () => (
    <ul className="flex flex-col md:flex-row w-full md:w-auto items-center space-y-4 md:space-y-0 md:space-x-5 md:mr-6">
      <li>
        <NavLink className={navLinkCls} to="/services" onClick={() => { trackEvent('navbar_services_click'); setIsMenuOpen(false) }}>
          {content.nav.services}
        </NavLink>
      </li>
      {content.nav.engineering && (
        <li>
          <NavLink
            className={navLinkCls}
            to="/forward-deployed-engineering"
            onClick={() => { trackEvent('navbar_engineering_click'); setIsMenuOpen(false) }}
          >
            {content.nav.engineering}
          </NavLink>
        </li>
      )}
      <li>
        <NavLink className={navLinkCls} to="/how-we-work" onClick={() => { trackEvent('navbar_hww_click'); setIsMenuOpen(false) }}>
          {content.nav.howWeWork}
        </NavLink>
      </li>
      {/* the flagship holds the nav slot; the full shelf stays in the footer */}
      <li>
        <NavLink className={navLinkCls} to="/maestro" onClick={() => { trackEvent('navbar_maestro_click'); setIsMenuOpen(false) }}>
          Maestro
        </NavLink>
      </li>
      <li>
        <NavLink className={navLinkCls} to="/insights" onClick={() => { trackEvent('navbar_insights_click'); setIsMenuOpen(false) }}>
          {content.nav.insights}
        </NavLink>
      </li>
      <li>
        <a
          href="/book"
          onClick={() => { trackEvent('navbar_book_click'); setIsMenuOpen(false) }}
          className="rounded-[8px] bg-pyramid-19 px-4 py-2 font-semibold text-[#101418] transition-all hover:bg-pyramid-19/85"
        >
          {cta.nav.book}
        </a>
      </li>
    </ul>
  )

  return (
    <nav className="fixed top-0 w-full z-50">
      <div
        className={`h-20 flex items-center justify-between px-6 md:px-16 transition-all duration-500 ${
          isScrolled
            ? 'bg-[rgba(17,22,28,0.9)] shadow-lg backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <a href="/" className="h-10 flex-shrink-0">
          <img src="/images/logo_leanit101.png" alt="Lean it 101" className="h-full object-contain" />
        </a>

        <button
          className="md:hidden text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle navigation"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
          </svg>
        </button>

        <div className="hidden md:flex items-center gap-3">
          <MenuOptions />
          <TranslationFlags isMobile={false} />
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-[#11161c]/95 backdrop-blur-md border-t border-white/5">
          <div className="py-6 px-6 flex flex-col items-center gap-4">
            <MenuOptions />
            <TranslationFlags isMobile={true} />
          </div>
        </div>
      )}
    </nav>
  )
}

export default NavigationBar
